import { cn } from '@shared/utils/utils';
import { useEffect, type MouseEvent, type SyntheticEvent } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Badge } from '@shared/components/ui/badge';
import { useProductOverlayStock } from './hooks/useProductOverlayStock';
import { ProductStockPanel } from './ProductStockPanel';

export interface ProductImageOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  src: string;
  name: string;
  code?: string;
  acronym?: string;
  /** Id del producto para cargar stock en la ficha */
  productId?: number;
  'data-testid'?: string;
}

export const ProductImageOverlay = ({
  isOpen,
  onClose,
  src,
  name,
  code,
  productId,
  'data-testid': testId = 'product-image-overlay',
}: Omit<ProductImageOverlayProps, 'acronym'>) => {
  const { t } = useTranslation('ui');
  const stock = useProductOverlayStock(productId, isOpen && Boolean(productId));

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape, true);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape, true);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const stopOverlayEvent = (event: SyntheticEvent) => {
    event.stopPropagation();
    const nativeEvent = event.nativeEvent as Event;
    nativeEvent.stopImmediatePropagation?.();
  };

  const handleBackdropClick = (event: MouseEvent<HTMLDivElement>) => {
    stopOverlayEvent(event);
    onClose();
  };

  const handleCloseClick = (event: MouseEvent<HTMLButtonElement>) => {
    stopOverlayEvent(event);
    onClose();
  };

  const overlayContent = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 pointer-events-auto"
      style={{ pointerEvents: 'auto' }}
      onPointerDownCapture={stopOverlayEvent}
      onMouseDownCapture={stopOverlayEvent}
      onClick={handleBackdropClick}
      data-testid={testId}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-lg" />

      <div
        className="relative animate-in zoom-in-95 duration-300 ease-out w-full max-w-4xl max-h-[95vh] flex flex-col pointer-events-auto overflow-hidden rounded-2xl border border-card-border bg-body-bg shadow-2xl"
        style={{ pointerEvents: 'auto' }}
        onClick={stopOverlayEvent}
      >
        <div className="flex items-start justify-between gap-3 p-4 border-b border-card-border">
          <div className="min-w-0 flex-1">
            <p className="font-medium text-title truncate mb-1">{name}</p>
            {code ? (
              <Badge variant="transparent-blue" className="mt-0 text-sm rounded-full" data-testid={`${testId}-code`}>
                {code}
              </Badge>
            ) : null}
          </div>
          <button
            type="button"
            onClick={handleCloseClick}
            className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-muted/80 text-muted-foreground hover:text-foreground transition-all duration-200 hover:scale-105 flex-shrink-0"
            data-testid={`${testId}-close`}
            aria-label={t('common.close')}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Ficha: imagen dominante + stock/ubicaciones más compacto */}
        <div className="flex flex-col md:flex-row gap-4 p-4 overflow-y-auto min-h-0">
          <div className="md:w-2/3 flex items-center justify-center bg-background rounded-xl border border-card-border overflow-hidden p-3 min-h-[220px]">
            <img
              src={src}
              alt={name}
              className={cn('block object-contain w-full h-auto max-w-full', 'max-h-[45vh] md:max-h-[60vh]')}
              data-testid={`${testId}-image`}
            />
          </div>

          {stock.canViewStock!==false && <div className="md:w-1/3 min-w-0 md:max-w-xs">
            <p className="text-sm font-medium text-title mb-1">{t('product_overlay.stock_section')}</p>
            {productId != null ? (
              <ProductStockPanel
                summary={stock.summary}
                locations={stock.locations}
                showLocations={stock.showLocations}
                isLoading={stock.isLoading}
                error={stock.error}
              />
            ) : (
              <p className="text-sm text-subtext" data-testid="product-stock-no-id">
                {t('product_overlay.stock_empty')}
              </p>
            )}
          </div>}
        </div>
      </div>
    </div>
  );

  return createPortal(overlayContent, document.body);
};
